import {
  getLeoUuid,
  LeoUUID,
  getObject,
  getLeoDate,
  LeoDate,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { KeycloakEmail, KeycloakEmailDTO } from "../types/keycloakEmail";
import { UserName, UserNameDTO } from "../types/userName";
/* eslint-disable import/export */
export abstract class AddSpocToProjectRPC implements
  LeoRPC<AddSpocToProjectRPC.Request, AddSpocToProjectRPC.Response, AddSpocToProjectRPC.Errors.Errors>
{
  abstract execute(
    request: AddSpocToProjectRPC.Request
  ): Promise<LeoRPCResult<AddSpocToProjectRPC.Response, AddSpocToProjectRPC.Errors.Errors>>;
}

export namespace AddSpocToProjectRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    spocEmailAddress: KeycloakEmailDTO;
    expiryDate: string;
    userName: UserNameDTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isAddSpocToProjectRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly spocEmailAddress: KeycloakEmail;
    readonly expiryDate: LeoDate;
    readonly userName: UserName;
  
  
    constructor(
    projectId: LeoUUID, 
    spocEmailAddress: KeycloakEmail, 
    expiryDate: LeoDate, 
    userName: UserName
    ) {
      this.projectId = projectId;
      this.spocEmailAddress = spocEmailAddress;
      this.expiryDate = expiryDate;
      this.userName = userName;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const spocEmailAddress = KeycloakEmail.fromDTO(getObject(dto, "spocEmailAddress", InvalidRequestSpocEmailAddressError));
      const expiryDate = getLeoDate(dto, "expiryDate", InvalidRequestExpiryDateError);
      const userName = UserName.fromDTO(getObject(dto, "userName", InvalidRequestUserNameError));
      return new Request(
        projectId,
        spocEmailAddress,
        expiryDate,
        userName
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          spocEmailAddress: this.spocEmailAddress.toDTO(),
          expiryDate: this.expiryDate.toDTO(),
          userName: this.userName.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `AddSpocToProjectRPC(projectId='${this.projectId}',spocEmailAddress='${this.spocEmailAddress}',expiryDate='${this.expiryDate}',userName='${this.userName}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `AddSpocToProjectRPC.Response()`;
  };

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    MaxSpocReached = "MAX_SPOC_REACHED",
    SpocAlreadyExists = "SPOC_ALREADY_EXISTS",
    InvalidExpiryDate = "INVALID_EXPIRY_DATE",
    BouCannotBeSpoc = "BOU_CANNOT_BE_SPOC",
    UnableToAddSpoc = "UNABLE_TO_ADD_SPOC"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `AddSpocToProjectRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `AddSpocToProjectRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface MaxSpocReachedDTO {
      code: string;
    }
    
    export class MaxSpocReached extends LeoRPCError<RPCError> {
      readonly isMaxSpocReached: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.MaxSpocReached;
        super(code);
      }
    
    
      toDTO(): MaxSpocReachedDTO {
        const dto: MaxSpocReachedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    MaxSpocReached.prototype.toString = function toString(): string {
      return `AddSpocToProjectRPC.MAX_SPOC_REACHED()`;
    };
    
    
    export interface SpocAlreadyExistsDTO {
      code: string;
    }
    
    export class SpocAlreadyExists extends LeoRPCError<RPCError> {
      readonly isSpocAlreadyExists: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.SpocAlreadyExists;
        super(code);
      }
    
    
      toDTO(): SpocAlreadyExistsDTO {
        const dto: SpocAlreadyExistsDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    SpocAlreadyExists.prototype.toString = function toString(): string {
      return `AddSpocToProjectRPC.SPOC_ALREADY_EXISTS()`;
    };
    
    
    export interface InvalidExpiryDateDTO {
      code: string;
    }
    
    export class InvalidExpiryDate extends LeoRPCError<RPCError> {
      readonly isInvalidExpiryDate: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidExpiryDate;
        super(code);
      }
    
    
      toDTO(): InvalidExpiryDateDTO {
        const dto: InvalidExpiryDateDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidExpiryDate.prototype.toString = function toString(): string {
      return `AddSpocToProjectRPC.INVALID_EXPIRY_DATE()`;
    };
    
    
    export interface BouCannotBeSpocDTO {
      code: string;
    }
    
    export class BouCannotBeSpoc extends LeoRPCError<RPCError> {
      readonly isBouCannotBeSpoc: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.BouCannotBeSpoc;
        super(code);
      }
    
    
      toDTO(): BouCannotBeSpocDTO {
        const dto: BouCannotBeSpocDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    BouCannotBeSpoc.prototype.toString = function toString(): string {
      return `AddSpocToProjectRPC.BOU_CANNOT_BE_SPOC()`;
    };
    
    
    export interface UnableToAddSpocDTO {
      code: string;
    }
    
    export class UnableToAddSpoc extends LeoRPCError<RPCError> {
      readonly isUnableToAddSpoc: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.UnableToAddSpoc;
        super(code);
      }
    
    
      toDTO(): UnableToAddSpocDTO {
        const dto: UnableToAddSpocDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    UnableToAddSpoc.prototype.toString = function toString(): string {
      return `AddSpocToProjectRPC.UNABLE_TO_ADD_SPOC()`;
    };
    

    export type Errors = InvalidProjectId | ProjectAlreadyArchived | MaxSpocReached | SpocAlreadyExists | InvalidExpiryDate | BouCannotBeSpoc | UnableToAddSpoc;
    export type ErrorsDTO = InvalidProjectIdDTO | ProjectAlreadyArchivedDTO | MaxSpocReachedDTO | SpocAlreadyExistsDTO | InvalidExpiryDateDTO | BouCannotBeSpocDTO | UnableToAddSpocDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.MaxSpocReached:
          return new MaxSpocReached();
        case RPCError.SpocAlreadyExists:
          return new SpocAlreadyExists();
        case RPCError.InvalidExpiryDate:
          return new InvalidExpiryDate();
        case RPCError.BouCannotBeSpoc:
          return new BouCannotBeSpoc();
        case RPCError.UnableToAddSpoc:
          return new UnableToAddSpoc();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends AddSpocToProjectRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestSpocEmailAddressError extends AddSpocToProjectRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSpocEmailAddressError";
  }
}

export class InvalidRequestExpiryDateError extends AddSpocToProjectRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestExpiryDateError";
  }
}

export class InvalidRequestUserNameError extends AddSpocToProjectRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestUserNameError";
  }
}


export class InvalidResponseInvalidProjectIdError extends AddSpocToProjectRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends AddSpocToProjectRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseMaxSpocReachedError extends AddSpocToProjectRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseMaxSpocReachedError";
  }
}

export class InvalidResponseSpocAlreadyExistsError extends AddSpocToProjectRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSpocAlreadyExistsError";
  }
}

export class InvalidResponseInvalidExpiryDateError extends AddSpocToProjectRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidExpiryDateError";
  }
}

export class InvalidResponseBouCannotBeSpocError extends AddSpocToProjectRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseBouCannotBeSpocError";
  }
}

export class InvalidResponseUnableToAddSpocError extends AddSpocToProjectRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseUnableToAddSpocError";
  }
}

